import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "reactstrap";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import Loader from "../../../Components/Common/Loader";
// import DeleteModal from "../../../Components/Common/DeleteModal";

// RTK Query hooks - ensure these exist in your slice
import {
  useGetTagsQuery,
  useGetTagsByIdQuery,
  useDeleteTagsByIdMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";

// Tag edit view component (similar to your MeasurementEditView)
import TagEditView from "./Tagseditview";
import { IsValidPermissions } from "Routes/AuthProtected";

interface Tag {
  id: number;
  name: string;
  created_at: string;
  modified_at?: string | null;
  description?: string | null;
}

interface TagsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Tag[];
}

const TagsList: React.FC = () => {
  // local UI state
  const [tagList, setTagList] = useState<Tag[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTagId, setCurrentTagId] = useState<number | null>(null);

  // view modal
  const [viewTagId, setViewTagId] = useState<number | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [pageState, setPageState] = useState({ page: 1, pageSize: 30, search: "" });
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const debounce = window.setTimeout(() => {
      setPageState((previous) => ({ ...previous, page: 1, search: searchInput.trim() }));
    }, 300);
    return () => window.clearTimeout(debounce);
  }, [searchInput]);

  // list hook
  const {
    data: tagsResponse,
    isLoading: tagsLoading,
    error: tagsError,
    refetch: refetchTags,
  } = useGetTagsQuery(pageState);

  // detail hook for view modal (skip when no id)
  const { data: tagDetail, isFetching: fetchingTagDetail } =
    useGetTagsByIdQuery(viewTagId ?? undefined, {
      skip: !viewTagId,
    });

  const [deleteTag] = useDeleteTagsByIdMutation();

  // keep local sorted by id
  useEffect(() => {
    if (tagsResponse?.results) {
      const sorted = [...tagsResponse.results].sort(
        (a, b) => Number(a.id) - Number(b.id)
      );
      setTagList(sorted);
    } else {
      setTagList([]);
    }
  }, [tagsResponse]);

  useEffect(() => {
    if (tagsError) toast.error("Failed to load tags");
    // if (deleteError) toast.error("Failed to delete tag");
  }, [tagsError /*, deleteError */]);

  // Edit/Create modal handlers
  const openCreateModal = () => {
    setCurrentTagId(null);
    setIsEditMode(false);
    setShowEditModal(true);
  };

  const openEditModal = (id: number) => {
    setCurrentTagId(id);
    setIsEditMode(true);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setCurrentTagId(null);
    setIsEditMode(false);
  };

  const handleSaved = () => {
    closeEditModal();
    refetchTags();
  };

  // View modal handlers
  const openViewModal = (id: number) => {
    setViewTagId(id);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setViewTagId(null);
  };

  // Delete flow handlers (example pattern)
  const onClickDelete = (tag: Tag) => {
    setSelectedTag(tag);
    setDeleteModalOpen(true);
  };

  const handleDelete = async (tag: Tag) => {
    if (!window.confirm(`Delete tag "${tag.name}"? This cannot be undone.`)) return;
    try {
      await deleteTag(tag.id).unwrap();
      toast.success("Tag deleted successfully.");
      refetchTags();
    } catch (error: any) {
      toast.error(error?.data?.detail || error?.data?.error || "Failed to delete tag.");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "id",
      },
      {
        Header: "Tag Name",
        accessor: "name",
        Cell: (row: any) => (
          <Link to={`/tags/${row.row.original.id}`} className="text-body">
            {row.row.original.name}
          </Link>
        ),
      },
      {
        Header: "Created",
        accessor: "created_at",
        Cell: (cell: any) =>
          new Date(cell.row.original.created_at).toLocaleDateString(),
      },
      {
        Header: "Modified",
        accessor: "modified_at",
        Cell: (cell: any) =>
          cell.row.original.modified_at
            ? new Date(cell.row.original.modified_at).toLocaleDateString()
            : "-",
      },
      {
        Header: "Action",
        Cell: (cellProps: any) => {
          const t: Tag = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                className="btn btn-soft-secondary btn-sm"
                tag="button"
              >
                <i className="ri-more-fill" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                {(IsValidPermissions(["VIEW_ALL_INVENTORY_TAG"]) || IsValidPermissions(["VIEW_ONLY_INVENTORY_TAG"])) &&
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openViewModal(t.id);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted" />{" "}
                    View
                  </DropdownItem>
                }
                {IsValidPermissions(["EDIT_INVENTORY_TAG"]) &&

                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openEditModal(t.id);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted" />{" "}
                    Edit
                  </DropdownItem>
                }

                {IsValidPermissions(["EDIT_INVENTORY_TAG"]) && (
                  <DropdownItem className="text-danger" onClick={() => handleDelete(t)}>
                    <i className="ri-delete-bin-line align-bottom me-2" />
                    Delete
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    []
  );

  // Quick fix: mark columns non-sortable (so header has no sort toggle)
  const colsWithDisable = useMemo(
    () => columns.map((c) => ({ ...c, disableSortBy: true })),
    [columns]
  );

  document.title = "Tags | Resource Inventory";

  if (tagsLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Tags" pageTitle="Resource Inventory" />
          <div className="text-center py-5">
            <Loader />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb title="Tags" pageTitle="Resource Inventory" />

        <Row>
          <Col xl={12}>
            <Card>
              <div className="card-header border-0 d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Tags</h5>
                <div className="d-flex align-items-center gap-2">
                  <div className="position-relative">
                    <Input
                      value={searchInput}
                      onChange={(event) => setSearchInput(event.target.value)}
                      placeholder="Search tags..."
                      aria-label="Search tags"
                    />
                  </div>
                  {IsValidPermissions(["ADD_INVENTORY_TAG"]) &&
                    <Button color="primary" onClick={openCreateModal}>
                      Add Tag
                    </Button>
                  }
                </div>
              </div>

              <div className="card-body pt-0">
                {tagList && tagList.length > 0 ? (
                  <TableContainer
                    columns={colsWithDisable}
                    data={tagList}
                    isGlobalFilter={false}
                    customPageSize={pageState.pageSize}
                    divClass="table-responsive mb-1"
                    tableClass="mb-0 align-middle table-borderless"
                    theadClass="table-light text-muted"
                    SearchPlaceholder="Search Tags..."
                    isPagination={false}
                    isServerPagination
                    serverPaginationProps={tagsResponse}
                    setPageState={setPageState}
                  />
                ) : (
                  <div className="py-4 text-center">
                    <div>
                      <i className="ri-search-line display-5 text-success"></i>
                    </div>
                    <div className="mt-4">
                      <h5>Sorry! No Tags Found</h5>
                      <p className="text-muted">
                        Click "Add Tag" to create one.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Edit/Create Modal */}
        <Modal isOpen={showEditModal} toggle={closeEditModal} size="lg">
          <ModalHeader toggle={closeEditModal}>
            {isEditMode ? "Edit Tag" : "Create Tag"}
          </ModalHeader>
          <ModalBody>
            <TagEditView
              tagId={currentTagId ?? undefined}
              compact={true}
              onSaved={handleSaved}
            />
          </ModalBody>
          {/* intentionally no ModalFooter here */}
        </Modal>

        {/* View Modal */}
        <Modal isOpen={isViewModalOpen} toggle={closeViewModal} size="lg">
          <ModalHeader toggle={closeViewModal}>Tag Details</ModalHeader>
          <ModalBody>
            {fetchingTagDetail ? (
              <div className="text-center py-4">
                <Loader />
              </div>
            ) : tagDetail ? (
              <div className="table-card">
                <table className="table mb-0">
                  <tbody>
                    <tr>
                      <td className="fw-medium">Name</td>
                      <td>{tagDetail.name}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Description</td>
                      <td>{tagDetail.description ?? "—"}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Created</td>
                      <td>{new Date(tagDetail.created_at).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Modified</td>
                      <td>
                        {tagDetail.modified_at
                          ? new Date(tagDetail.modified_at).toLocaleString()
                          : "-"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No details available.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeViewModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* Delete Modal (optional) */}
        {/* <DeleteModal
          show={deleteModalOpen}
          title="Delete Tag"
          message={
            selectedTag
              ? `Are you sure you want to delete "${selectedTag.name}"?`
              : "Are you sure you want to delete this tag?"
          }
          onCloseClick={() => {
            setDeleteModalOpen(false);
            setSelectedTag(null);
          }}
          onDeleteClick={handleDelete} // uncomment when you implement delete
        /> */}
      </Container>
    </div>
  );
};

export default TagsList;
